import type { TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueByOpp, validReqAges, splitUserSCNames } from '../../utils/opportunities';
import { RDOTS, SUB_REGIONS } from '../../utils/regions';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { SectionHeader } from '../ui/SectionHeader';
import { TrendChart } from '../charts/TrendChart';
import { AccountBySolutionChart } from '../charts/AccountBySolutionChart';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { useRequests } from '../../context/RequestsContext';
import { useState } from 'react';
interface Props {
  state: TabState & {
    status: string;
  };
  onDrillRegion: (reg: string, sub: string) => void;
}
export function VPPanel({
  state,
  onDrillRegion
}: Props) {
  const {
    requests
  } = useRequests();
  const [offeringTop, setOfferingTop] = useState(8);
  const [offeringTotal, setOfferingTotal] = useState(1);
  const all = getFiltered('vp', state, requests);
  const ip = all.filter(r => r.req_status === 'In Progress');
  const cls = all.filter(r => r.req_status === 'Closed');
  const onh = all.filter(r => r.req_status === 'On Hold');
  const can = all.filter(r => r.req_status === 'Cancelled');
  const scs = new Set(ip.flatMap(r => splitUserSCNames(r.user_sc_name))).size;
  const pipelineOppGnarr = sumUniqueByOpp(all, r => r.opp_gnarr);
  const ages = validReqAges(all);
  const titleLbl = state.sub && state.sub !== 'All' ? state.sub : state.r;
  const crumb = state.sub && state.sub !== 'All';

  // Region/sub breakdown
  const showSub = state.r !== 'Global' && !!SUB_REGIONS[state.r];
  const regMap: Record<string, {
    ip: number;
    gnarr: number;
    closed: number;
    onhold: number;
    book: number;
    parent: string;
    cancelled: number;
  }> = {};
  all.forEach(r => {
    const key = showSub ? r.req_sub_region : r.opp_region;
    if (!regMap[key]) regMap[key] = {
      ip: 0,
      gnarr: 0,
      closed: 0,
      onhold: 0,
      book: 0,
      parent: r.opp_region,
      cancelled: 0
    };
    regMap[key].book++;
    if (r.req_status === 'In Progress') {
      regMap[key].ip++;
      regMap[key].gnarr += r.opp_gnarr_grand_total;
    } else if (r.req_status === 'Closed') regMap[key].closed++;else if (r.req_status === 'On Hold') regMap[key].onhold++;else if (r.req_status === 'Cancelled') regMap[key].cancelled++;
  });
  const maxIP = Math.max(...Object.values(regMap).map(r => r.ip), 1);
  const ALLOWED_REGIONS = new Set(['AMER', 'EMEA', 'JAPAC', '']);
  const regRows = Object.entries(regMap).filter(([reg]) => showSub || ALLOWED_REGIONS.has(reg)).sort((a, b) => b[1].ip - a[1].ip);
  interface RegionRow {
    reg: string;
    ip: number;
    gnarr: number;
    closed: number;
    book: number;
    onhold: number;
    parent: string;
  }
  const regionData: RegionRow[] = regRows.map(([reg, d]) => ({
    reg,
    ip: d.ip,
    gnarr: d.gnarr,
    closed: d.closed,
    book: d.book,
    onhold: d.onhold,
    parent: d.parent
  }));
  const regionColumns: SortableColumn<RegionRow>[] = [{
    key: 'reg',
    label: 'Region',
    width: '14%',
    sortValue: r => r.reg,
    render: r => <>
          <span className="rdot" style={{
        background: RDOTS[r.reg] || '#888'
      }} data-id="element-902" data-mp="361i22a6l5rlq" />
          {r.reg || 'No DR'}
        </>
  }, {
    key: 'ip',
    label: 'In-progress',
    width: '26%',
    sortValue: r => r.ip,
    render: r => <>
          <div className="pbg" style={{
        width: 90
      }} data-id="element-903" data-mp="3fqtbjxxdwya5">
            <div className="pf" style={{
          width: `${Math.round(r.ip / maxIP * 100)}%`
        }} data-id="element-904" data-mp="3kpcxhybzaa2i" />
          </div>
          <strong data-id="element-905" data-mp="3poz6twoor661">{r.ip}</strong>
        </>
  }, {
    key: 'gnarr',
    label: 'Pipeline GNARR',
    width: '16%',
    sortValue: r => r.gnarr,
    render: r => <>{fmtG(r.gnarr)}</>
  }, {
    key: 'closed',
    label: 'Closed',
    width: '12%',
    sortValue: r => r.closed,
    render: r => <span style={{
      color: r.closed > 0 ? '#15803D' : '#344054',
      fontWeight: r.closed > 0 ? 600 : 400
    }} data-id="element-906" data-mp="1zduo8vv7e36q">{r.closed}</span>
  }, {
    key: 'book',
    label: 'Total',
    width: '12%',
    sortValue: r => r.book,
    render: r => <>{r.book}</>
  }, {
    key: 'onhold',
    label: 'On hold',
    width: '10%',
    sortValue: r => r.onhold,
    render: r => <>{r.onhold}</>
  }, {
    key: 'parent',
    label: 'Sub-region',
    width: '10%',
    sortValue: r => r.parent,
    render: r => showSub ? <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-907" data-mp="lcwx5qdesiqg">{r.parent}</span> : <>—</>
  }];

  // Book status bar — "In Progress" uses Adobe red
  const total = ip.length + cls.length + onh.length + can.length || 1;
  const segs = [{
    l: 'Closed',
    v: cls.length,
    c: '#34d399'
  }, {
    l: 'In Progress',
    v: ip.length,
    c: '#fa0f00'
  }, {
    l: 'On Hold',
    v: onh.length,
    c: '#d4a017'
  }, {
    l: 'Cancelled',
    v: can.length,
    c: '#f87171'
  }];

  // Close quarter grid
  const cqMap: Record<string, {
    n: number;
    g: number;
  }> = {};
  ip.forEach(r => {
    if (!cqMap[r.opp_close_q]) cqMap[r.opp_close_q] = {
      n: 0,
      g: 0
    };
    cqMap[r.opp_close_q].n++;
    cqMap[r.opp_close_q].g += r.opp_gnarr_grand_total;
  });
  const cqShow = Object.keys(cqMap).filter(q => q.includes('2026') || q === '2027-Q1').sort().slice(0, 3);
  const qColors = ['#d4a017', '#fa0f00', '#34d399'];
  return <div data-id="element-908" data-mp="24b2z33hb5orm">
      {/* Metrics */}
      <div className="mrow" style={{
      gridTemplateColumns: 'repeat(5, 1fr)'
    }} data-id="element-909" data-mp="2s0e10oae9p7c">
       <MetricCard label="Total requests" value={ip.length + cls.length} sub={`Closed + In progress `} colorClass="c-green" data-id="element-910" data-mp="2ryb0vbfj2fow" />
       <MetricCard label="Closed requests" value={cls.length} colorClass="c-green" data-id="element-911" data-mp="2izzexmplw62s" />
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} active SCs`} colorClass="c-blue" data-id="element-912" data-mp="2nysionf3cipr" />
        <MedianAgeCard ages={ages} data-id="element-913" data-mp="17ktylfwjy90p" />
        <MetricCard label="GNARR Influenced" value={fmtG(pipelineOppGnarr)} sub={`Closed & In-progress requests`} colorClass="c-green" data-id="element-914" data-mp="17jrb7hyfuopj" />
      </div>

      {/* Charts row: Offering (left) + Trend (right) */}
      <div style={{
      display: 'flex',
      gap: 12
    }} data-id="element-915" data-mp="3e8b5kmou3vlt">
        <div style={{
        flex: 1,
        minWidth: 0
      }} data-id="element-916" data-mp="399zvcaumnj32">
          <SectionHeader title="Accounts by solution" data-id="element-917" data-mp="39a9na3z8g0be" />
          <div className="card" style={{
          minHeight: 260
        }} data-id="element-918" data-mp="34irif4u39fl5">
            <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4
          }} data-id="element-919" data-mp="34gwy9sqn5ri4">
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 9,
              color: '#667085'
            }} data-id="element-920" data-mp="2vibjm8oaiwmm">
                <span data-id="element-921" data-mp="2qji5qf53d3lx">Top</span>
                <input type="range" min={1} max={offeringTotal} value={offeringTop} onChange={e => setOfferingTop(Number(e.target.value))} style={{
                width: 80,
                accentColor: '#3B82F6'
              }} data-id="element-922" data-mp="2qi70eavzznsp" />
                <span style={{
                minWidth: 20,
                textAlign: 'right'
              }} data-id="element-923" data-mp="jsser7k2tsoc">{offeringTop + 1}</span>
              </div>
            </div>
            <AccountBySolutionChart requests={all} top={offeringTop} onTopChange={setOfferingTop} onTotalChange={setOfferingTotal} chartHeight={224} data-id="element-924" data-mp="fqnlwb1gd02r" />
          </div>
        </div>
        <div style={{
        flex: 1,
        minWidth: 0
      }} data-id="element-925" data-mp="22nwuwwz6vie4">
          <SectionHeader title={<>{titleLbl} — request volume &amp; GNARR by Created Quarter{crumb && <span className="region-crumb" data-id="element-927" data-mp="27f59e7fdlty5">{state.sub}</span>}</>} data-id="element-926" data-mp="22mlrwzdkieo2" />
          <div className="card" style={{
          minHeight: 260
        }} data-id="element-928" data-mp="2bf5kpj76hp0z">
            <TrendChart region={state.r} sub={state.sub} status={state.status} requests={requests.filter(r => r.req_status === 'In Progress' || r.req_status === 'Closed')} data-id="element-929" data-mp="1epxzydedvjy4" />
          </div>
        </div>
      </div>

      {/* Region breakdown table */}
      <SectionHeader title={<>Business impact by region — {titleLbl}{crumb && <span className="region-crumb" data-id="element-931" data-mp="1spz9eh8przp7">{state.sub}</span>}</>} data-id="element-930" data-mp="1npuv8xva3h25" />
      <div className="tw" data-id="element-932" data-mp="2z5t4p2udc53w">
        <div className="sub" data-id="element-933" data-mp="2z6kqxn22sqhi">
          In-progress load, pipeline GNARR, and deals closed. Click a region row to drill into SC Leaders view.
        </div>
        <SortableTable columns={regionColumns} data={regionData} rowKey={r => r.reg} defaultSort={{
        key: 'ip',
        dir: 'desc'
      }} onRowClick={r => onDrillRegion(r.parent || r.reg, showSub ? r.reg : '')} data-id="element-934" data-mp="346pc3qdxi9e2" />
      </div>

      {/* Book status + CQ grid */}
      {/* <div className="br2">
        <div className="bcard">
          <div className="sh" style={{ padding: '0 0 8px' }}>
            <div className="shb" />
            <span className="sht">Book status mix</span>
          </div>
          <div className="book-bar">
            {segs.map(s => (
              <div key={s.l} style={{ width: `${Math.round(s.v / total * 100)}%`, height: '100%', background: s.c }} />
            ))}
          </div>
          <div className="bleg">
            {segs.map(s => (
              <span key={s.l}>
                <span className="bldot" style={{ background: s.c }} />
                {s.l} {s.v}
              </span>
            ))}
          </div>
        </div>
        <div className="bcard">
          <div className="sh" style={{ padding: '0 0 6px' }}>
            <div className="shb" />
            <span className="sht">In progress by close quarter</span>
          </div>
          <div className="cqg">
            {cqShow.map((q, i) => (
              <div key={q} className="cqc">
                <div className="cql">{q}</div>
                <div className="cqn">{cqMap[q].n}</div>
                <div className="cqs">in-progress deals</div>
                <div className="cqs">{fmtG(cqMap[q].g)} pipeline</div>
                <div className="cqp" style={{ background: qColors[i] || '#4f9cf9' }} />
              </div>
            ))}
          </div>
        </div>
       </div> */}

    </div>;
}